import { ApiBaseService, ApiV2Service } from './api.base.service';
import {DeviceVersionInterface, DeviceFunction, ConfigurationProfile, DeviceType, DeviceModel, ChipManufacturer, Manufacturer, Network, UploadImageUrlResponse, ModbusRegisterConfig, IdResponse, SuccessResponse} from '@/types';

export class AdminDeviceTypeService {
  private api: ApiBaseService;

  constructor() {
    this.api = ApiV2Service;
  }

  public async getSystemDeviceType(deviceTypeId: number): Promise<DeviceType> {
    return this.api.get(`admin/deviceVersions/${deviceTypeId}`);
  }

  public async getSystemDeviceTypes(): Promise<DeviceType[]> {
    return this.api.get(`admin/deviceVersions`);
  }

  public async getDeviceModels(): Promise<DeviceModel[]> {
    return this.api.get(`admin/deviceModels`);
  }

  public async getChipManufacturers(): Promise<ChipManufacturer[]> {
    return this.api.get(`admin/chipManufacturers`);
  }

  public async getManufacturers(): Promise<Manufacturer[]> {
    return this.api.get(`admin/manufacturers`);
  }

  public async getNetworks(): Promise<Network[]> {
    return this.api.get(`admin/networks`);
  }

  public async saveSystemDeviceType(deviceType: any[]): Promise<IdResponse> {
    return this.api.post(`admin/deviceVersions/add`, deviceType);
  }

  public async updateSystemDeviceType(id: number, payload: object): Promise<IdResponse> {
    return this.api.put(`admin/deviceVersions/${id}`, payload);
  }

  public async deleteSystemDevices(devices: any[], deleteReason: string): Promise<SuccessResponse> {
    const payload = {deviceIds: devices, deleteReason};
    return this.api.delete(`admin/deviceVersions`, payload);
  }

  public async createSystemConfigurationProfile(versionId: number, profile: object): Promise<IdResponse> {
    return this.api.post(`admin/deviceVersions/${versionId}/configurationProfiles`, profile);
  }

  public async updateSystemConfigurationProfile(versionId: number, profileId: number, profile: object): Promise<IdResponse> {
    return this.api.put(`admin/deviceVersions/${versionId}/configurationProfiles/${profileId}`, profile);
  }

  public async deleteSystemConfigurationProfile(versionId: number, profileId: number): Promise<SuccessResponse> {
    return this.api.delete(`admin/deviceVersions/${versionId}/configurationProfiles/${profileId}`);
  }

  public async getSystemConfigurationProfiles(versionId: number): Promise<ConfigurationProfile[]> {
    return this.api.get(`admin/deviceVersions/${versionId}/configurationProfiles`);
  }

  public async addManufacturer(manufacturer: any): Promise<IdResponse> {
    return this.api.post('admin/manufacturers', manufacturer);
  }

  public async addChipManufacturer(manufacturer: any): Promise<IdResponse> {
    return this.api.post('admin/chipManufacturers', manufacturer);
  }

  public async addDeviceModel(deviceModel: any): Promise<IdResponse> {
    return this.api.post('admin/deviceModels', deviceModel);
  }

  public async uploadDeviceLogo(id: number, file: Blob): Promise<any> {
    const urlResp = await this.api.get<UploadImageUrlResponse>(`admin/deviceVersion/${id}/logo-upload-url`);
    const data = urlResp.urlInfo;
    const formData = new FormData();
    // formData.append('Content-Type', file.type);
    Object.entries(data.fields).forEach(([k, v]) => {
      formData.append(k, (v as string));
    });
    formData.append('file', file);
    return this.api.postFormExt(data.url, formData);
  }

  public async removeDeviceLogo(id: number): Promise<SuccessResponse> {
    return this.api.delete(`admin/deviceVersion/${id}/logo`);
  }

  public async updateDeviceFunctionForInterface(id: number, body: Partial<DeviceFunction>): Promise<IdResponse> {
    return this.api.put(`admin/deviceVersion/interfaceFunctions/${id}`, body);
  }

  public async deleteMapping(id: number, dviId: number): Promise<SuccessResponse> {
    return this.api.delete(`admin/deviceVersion/${id}/interfaceMappings/${dviId}`);
  }

  public async getAllInterfaceMappings(id: number): Promise<DeviceVersionInterface[]> {
    return this.api.get(`admin/deviceVersion/${id}/interfaceMappings`);
  }

  public async getAllMappingsForInterfaceId(id: number): Promise<DeviceVersionInterface[]> {
    return this.api.get(`admin/deviceVersion/interfaceMappings/${id}`);
  }

  public async addInterfaceMappingWithFunction(id: number, body: Partial<DeviceVersionInterface>): Promise<DeviceVersionInterface> {
    return this.api.post(`admin/deviceVersion/${id}/interfaceMappings/add`, body);
  }

  public async getModbusRegisterConfigs(deviceVersionId: number): Promise<ModbusRegisterConfig[]> {
    return this.api.get(`admin/deviceVersion/${deviceVersionId}/modbusRegisters`);
  }

  public async setModbusRegisterConfigs(deviceVersionId: number, registers: any[]): Promise<ModbusRegisterConfig[]> {
    return this.api.post(`admin/deviceVersion/${deviceVersionId}/modbusRegisters`, { registers });
  }
}

export default new AdminDeviceTypeService();
